/**
 * MaintenanceCalendarPage — `/admin/maintenance/calendar`
 *
 * Every facility due for servicing across the whole organisation, laid out a
 * month at a time with a 12-month strip on top so the year can be planned in
 * one sitting. Narrow it to any branch of the hierarchy with the scope picker.
 */
import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import api, { getErrorMessage } from '@/lib/api';
import { isoDay } from '@/lib/localDate';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import {
  ArrowLeft, CalendarClock, ChevronLeft, ChevronRight, Wrench, CheckCircle2, AlertTriangle,
} from 'lucide-react';

const WEEKDAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

const STATUS = {
  overdue: { dot: 'bg-rose-500', chip: 'bg-rose-50 text-rose-700 border-rose-200', label: 'Overdue' },
  due_soon: { dot: 'bg-amber-500', chip: 'bg-amber-50 text-amber-700 border-amber-200', label: 'Due this week' },
  scheduled: { dot: 'bg-indigo-500', chip: 'bg-indigo-50 text-indigo-700 border-indigo-200', label: 'Scheduled' },
  serviced: { dot: 'bg-emerald-500', chip: 'bg-emerald-50 text-emerald-700 border-emerald-200', label: 'Serviced' },
};

/** Monday-first grid of the given month, padded to whole weeks. */
const buildGrid = (year, month) => {
  const first = new Date(Date.UTC(year, month - 1, 1));
  const lead = (first.getUTCDay() + 6) % 7;
  const total = new Date(Date.UTC(year, month, 0)).getUTCDate();
  const cells = [];
  for (let i = 0; i < lead; i += 1) cells.push(null);
  for (let d = 1; d <= total; d += 1) {
    cells.push(`${year}-${String(month).padStart(2, '0')}-${String(d).padStart(2, '0')}`);
  }
  while (cells.length % 7 !== 0) cells.push(null);
  return cells;
};

const MaintenanceCalendarPage = () => {
  const today = new Date();
  const [year, setYear] = useState(today.getFullYear());
  const [month, setMonth] = useState(today.getMonth() + 1);
  const [scopeId, setScopeId] = useState('');
  // Local day, not UTC: `toISOString()` highlighted yesterday's cell as
  // "today" for every user east of Greenwich before 02:00.
  const todayIso = isoDay(today);

  const { data: orgs } = useQuery({
    queryKey: ['org-list-for-calendar'],
    queryFn: async () => (await api.get('/admin/organisations')).data,
  });

  const { data, isLoading, error } = useQuery({
    queryKey: ['maintenance-calendar', year, month, scopeId],
    queryFn: async () => (await api.get('/church/maintenance/calendar',
      { params: { year, month, scope_id: scopeId } })).data,
    retry: false,
  });

  const grid = useMemo(() => buildGrid(year, month), [year, month]);
  const days = data?.days || {};
  const totals = data?.totals || {};

  const step = (delta) => {
    const next = month + delta;
    if (next < 1) { setMonth(12); setYear(year - 1); } else if (next > 12) { setMonth(1); setYear(year + 1); } else setMonth(next);
  };

  const scopeOptions = (orgs || []).filter((o) =>
    ['global_ministry', 'country', 'province', 'district'].includes(o.organisation_type));

  return (
    <div className="min-h-screen bg-slate-50" data-testid="maintenance-calendar-page">
      <header className="border-b bg-white">
        <div className="mx-auto flex max-w-6xl flex-wrap items-center justify-between gap-3 px-4 py-4">
          <div className="flex items-center gap-3">
            <Link to="/admin/churches" className="text-slate-400 hover:text-slate-700"
                  data-testid="calendar-back">
              <ArrowLeft className="h-5 w-5" />
            </Link>
            <div>
              <h1 className="flex items-center gap-2 text-xl font-bold text-slate-900">
                <CalendarClock className="h-5 w-5 text-amber-600" /> Maintenance calendar
              </h1>
              <p className="text-xs text-slate-500" data-testid="calendar-scope-label">
                {data?.scope?.name || 'Whole organisation'}
              </p>
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <select value={scopeId} onChange={(e) => setScopeId(e.target.value)}
                    data-testid="calendar-scope-select"
                    className="h-8 max-w-[240px] rounded-md border border-slate-200 bg-white px-2 text-xs">
              <option value="">Whole organisation</option>
              {scopeOptions.map((o) => (
                <option key={o.id} value={o.id}>{o.name}</option>
              ))}
            </select>
            <div className="flex items-center gap-1">
              <Button size="icon" variant="outline" className="h-8 w-8" onClick={() => step(-1)}
                      data-testid="calendar-prev-month"><ChevronLeft className="h-4 w-4" /></Button>
              <span className="min-w-0 sm:min-w-[110px] text-center text-sm font-semibold text-slate-800"
                    data-testid="calendar-month-label">{data?.month_label || '—'}</span>
              <Button size="icon" variant="outline" className="h-8 w-8" onClick={() => step(1)}
                      data-testid="calendar-next-month"><ChevronRight className="h-4 w-4" /></Button>
            </div>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-6xl space-y-4 px-4 py-6">
        {error && (
          <Card data-testid="calendar-error"><CardContent className="py-10 text-center text-sm text-slate-600">
            {getErrorMessage(error)}
          </CardContent></Card>
        )}

        {/* Year strip */}
        <Card data-testid="calendar-year-strip">
          <CardContent className="flex flex-wrap gap-2 py-4">
            {(data?.year_counts || []).map((m) => (
              <button key={m.month} onClick={() => setMonth(m.month)}
                      data-testid={`calendar-year-month-${m.month}`}
                      className={`flex min-w-[74px] flex-col items-start rounded-lg border px-3 py-2 text-left transition-colors ${
                        m.month === month ? 'border-slate-900 bg-slate-900 text-white'
                          : 'border-slate-200 hover:border-slate-300 hover:bg-slate-50'}`}>
                <span className="text-[10px] font-semibold uppercase tracking-wide opacity-70">{m.label}</span>
                <span className="text-lg font-bold tabular-nums">{m.count}</span>
              </button>
            ))}
          </CardContent>
        </Card>

        <div className="flex flex-wrap gap-2">
          {[['due', totals.due, 'Due this month', Wrench],
            ['overdue', totals.overdue, 'Overdue', AlertTriangle],
            ['serviced', totals.serviced, 'Serviced', CheckCircle2]].map(([key, value, label, Icon]) => (
            <Badge key={key} variant="outline" className="gap-1.5 px-3 py-1.5 text-xs"
                   data-testid={`calendar-total-${key}`}>
              <Icon className="h-3.5 w-3.5 text-slate-400" />
              <span className="font-semibold tabular-nums">{value ?? 0}</span> {label}
            </Badge>
          ))}
        </div>

        {/* Month grid */}
        <Card>
          <CardContent className="p-3">
            {isLoading ? (
              <Skeleton className="h-[420px] w-full" />
            ) : (
              <>
                <div className="mb-1 grid grid-cols-7 gap-1">
                  {WEEKDAYS.map((d) => (
                    <p key={d} className="px-2 py-1 text-[10px] font-semibold uppercase tracking-wide text-slate-400">{d}</p>
                  ))}
                </div>
                <div className="grid grid-cols-7 gap-1">
                  {grid.map((iso, i) => (
                    <div key={iso || `pad-${i}`}
                         data-testid={iso ? `calendar-day-${iso}` : undefined}
                         className={`min-h-[92px] rounded-lg border p-1.5 ${
                           !iso ? 'border-transparent'
                             : iso === todayIso ? 'border-slate-900 bg-white'
                             : 'border-slate-100 bg-white'}`}>
                      {iso && (
                        <>
                          <p className={`px-1 text-[11px] ${iso === todayIso ? 'font-bold text-slate-900' : 'text-slate-400'}`}>
                            {Number(iso.slice(8))}
                          </p>
                          <div className="space-y-1">
                            {(days[iso] || []).slice(0, 3).map((it) => (
                              <div key={it.id} title={`${it.name} · ${it.building} · ${it.church_name}`}
                                   data-testid={`calendar-entry-${it.id}`}
                                   className={`truncate rounded border px-1.5 py-0.5 text-[10px] ${STATUS[it.status].chip}`}>
                                <span className={`mr-1 inline-block h-1.5 w-1.5 rounded-full align-middle ${STATUS[it.status].dot}`} />
                                {it.name}
                              </div>
                            ))}
                            {(days[iso] || []).length > 3 && (
                              <p className="px-1 text-[10px] text-slate-400">
                                +{days[iso].length - 3} more
                              </p>
                            )}
                          </div>
                        </>
                      )}
                    </div>
                  ))}
                </div>
              </>
            )}
          </CardContent>
        </Card>

        {/* Flat list for the month — what the campus team works from */}
        <Card>
          <CardContent className="p-0">
            {(data?.items || []).length === 0 ? (
              <p className="py-14 text-center text-sm text-slate-500" data-testid="calendar-empty">
                Nothing scheduled in {data?.month_label || 'this month'}. Add a next service date to
                a facility and it will appear here.
              </p>
            ) : (
              <div className="divide-y divide-slate-100">
                {data.items.map((it) => (
                  <div key={it.id} className="flex flex-wrap items-center justify-between gap-3 px-4 py-3"
                       data-testid={`calendar-list-${it.id}`}>
                    <div className="flex min-w-0 items-center gap-3">
                      <span className={`h-2 w-2 shrink-0 rounded-full ${STATUS[it.status].dot}`} />
                      <div className="min-w-0">
                        <p className="truncate text-sm font-medium text-slate-800">
                          {it.name}
                          <span className="ml-2 text-xs font-normal text-slate-400">{it.building}</span>
                        </p>
                        <p className="text-[11px] text-slate-500">
                          {it.church_name} · {it.date}
                          {it.kind === 'due' && (
                            it.days_until < 0 ? ` · ${Math.abs(it.days_until)} day(s) overdue`
                              : it.days_until === 0 ? ' · due today' : ` · in ${it.days_until} day(s)`)}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Badge variant="outline" className={`text-[10px] ${STATUS[it.status].chip}`}>
                        {STATUS[it.status].label}
                      </Badge>
                      <Link to={`/admin/churches/${it.church_id}`}
                            className="text-[11px] font-medium text-indigo-600 hover:underline"
                            data-testid={`calendar-open-${it.id}`}>
                        Open campus
                      </Link>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </main>
    </div>
  );
};

export default MaintenanceCalendarPage;
